/* Shared utility to cast enriched feed items from Convex (which use [key: string]: unknown spread)
   into the typed FeedItemData shape used by UI components. */

import type { FeedItemData } from "@/components/feed/FeedItemRow";
import type { Id } from "../../convex/_generated/dataModel";

type RawItem = Record<string, unknown> & { _id: Id<"feedItems"> };

export function castFeedItems(
	raw: RawItem[],
): (FeedItemData & { _id: Id<"feedItems"> })[] {
	return raw.map((r) => ({
		_id: r._id as Id<"feedItems">,
		title: r.title as string,
		publishedAt: r.publishedAt as number,
		link: r.link as string,
		thumbnail: (r.thumbnail as string | undefined) ?? undefined,
		sourceName: (r.sourceName as string | undefined) ?? undefined,
		sourceTags: (r.sourceTags as string[] | undefined) ?? undefined,
		contentType: (r.contentType as string | undefined) ?? undefined,
		sourceStarred: (r.sourceStarred as boolean | undefined) ?? undefined,
		duration: (r.duration as number | undefined) ?? undefined,
		isProvocative: (r.isProvocative as boolean | undefined) ?? undefined,
		note: (r.note as string | undefined) ?? undefined,
		itemTags: (r.itemTags as string[] | undefined) ?? undefined,
		bookAuthor: (r.bookAuthor as string | undefined) ?? undefined,
		bookTotalPages: (r.bookTotalPages as number | undefined) ?? undefined,
		bookCover: (r.bookCover as string | undefined) ?? undefined,
		bookPagesPerDay: (r.bookPagesPerDay as number | undefined) ?? undefined,
		bookCurrentPage: (r.bookCurrentPage as number | undefined) ?? undefined,
		bookPageStart: (r.bookPageStart as number | undefined) ?? undefined,
		bookPageEnd: (r.bookPageEnd as number | undefined) ?? undefined,
		movieImdbId: (r.movieImdbId as string | undefined) ?? undefined,
		movieDirector: (r.movieDirector as string | undefined) ?? undefined,
		movieYear: (r.movieYear as string | undefined) ?? undefined,
		movieGenres: (r.movieGenres as string | undefined) ?? undefined,
		movieRuntime: (r.movieRuntime as string | undefined) ?? undefined,
		movieRating: (r.movieRating as string | undefined) ?? undefined,
		moviePoster: (r.moviePoster as string | undefined) ?? undefined,
		moviePlot: (r.moviePlot as string | undefined) ?? undefined,
		movieActors: (r.movieActors as string | undefined) ?? undefined,
		audioUrl: (r.audioUrl as string | undefined) ?? undefined,
		sourceId: (r.sourceId as string | undefined) ?? undefined,
		userSubscriptionId: (r.userSubscriptionId as string | undefined) ?? undefined,
		sourceSkipProvocationCheck: (r.sourceSkipProvocationCheck as boolean | undefined) ?? undefined,
		aiTopics: (r.aiTopics as string[] | undefined) ?? undefined,
		playlistName: (r.playlistName as string | undefined) ?? undefined,
		playlistVideoPosition: (r.playlistVideoPosition as number | undefined) ?? undefined,
		playlistTotalVideos: (r.playlistTotalVideos as number | undefined) ?? undefined,
		tvEpisodeTitle: (r.tvEpisodeTitle as string | undefined) ?? undefined,
		tvSeason: (r.tvSeason as number | undefined) ?? undefined,
		tvEpisode: (r.tvEpisode as number | undefined) ?? undefined,
		tvShowTitle: (r.tvShowTitle as string | undefined) ?? undefined,
		tvShowPoster: (r.tvShowPoster as string | undefined) ?? undefined,
		tvShowNetwork: (r.tvShowNetwork as string | undefined) ?? undefined,
	}));
}

export type { RawItem };
